import { useContext } from 'react';
import { db } from '../firebaseConfig';
import moment from 'moment';
import UserContext from 'contexts/userContext';
import { collection, query, where, doc, updateDoc, getDocs, orderBy, Timestamp} from "firebase/firestore";


// FuFunction to get first set of key registry data based on search parameters
export const getAllKeysByBuildingId = async(buildingId, keyStatus, keyColorValue) => {
    const docRef = collection(db, "allKeys");
    let keyQuery = query(docRef, orderBy('tag', 'asc'), 
        where("buildingID", "==", buildingId),
        where("status", "in", keyStatus));
    if(keyColorValue != '') {
        keyQuery = query(keyQuery, where("colour", "==", keyColorValue));
    }
    const querySnapshot = await getDocs(keyQuery);
    let resultArray = [];
    querySnapshot.forEach((doc) => {
        const key = doc.data();
        resultArray.push({
            number : key.number,
            tag : key.tag,
            status : key.status ? 'In-Use' : 'Free',
            color : key.colour
        })
    });
    return {result : resultArray};  
}

// FuFunction to get first set of key registry data based on search parameters
export const getAllKeysData = async(buildingId) => {
    const docRef = collection(db, "allKeys");
    let keyQuery = query(docRef, orderBy('tag', 'asc'), 
        where("buildingID", "==", buildingId));
        
    const querySnapshot = await getDocs(keyQuery);
    let resultArray = [];
    querySnapshot.forEach((doc) => {
        const key = doc.data();
        resultArray.push({
            number : key.number,
            tag : key.tag,
            status : key.status ? 'In-Use' : 'Free',
            color : key.colour
        })
    });
    return {result : resultArray};  
}


// Function to get first set of outstanding keys data based on search parameters
export const getAllOutStandingKeys = async (buildingId) => {
    const docRef = collection(db, "records");
    let keyQuery = query(docRef,
        where("buildingID", "==", buildingId),
        orderBy('issueTime', 'desc'));
    const querySnapshot = await getDocs(keyQuery);
    let resultArray = [];
    querySnapshot.forEach((doc) => {
        const key = doc.data();
        const id = doc.id;
        resultArray.push({
            issueTime : moment(key.issueTime.toDate()).format('MM/DD/YYYY hh:mm a'),
            visitorCompany : key.visitorCompany,
            visitorEmail : key.visitorEmail,
            visitorName : key.visitorName,
            visitorPhone : key.visitorPhoneNo,
            keyNumber : key.keyNumber,
            tag : key.keyTag,
            status : key.status,
            location : key.location,
            notes : key.notes ? key.notes : [],
            id: id
        })
    });
    return {result : resultArray};
}

// Helper function to split an array into chunks
const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
};


export const getOutstandingKeysForMultipleBuildings = async (buildingIds) => {
    if (!buildingIds || buildingIds.length === 0) return { result: [] };

    const recordsRef = collection(db, "records");
    const buildingsRef = collection(db, "buildings");
    const chunks = chunkArray(buildingIds, 10);

    try {
        // --- STEP 1: Fetch Building Names in Parallel ---
        const buildingQueryPromises = chunks.map(chunk => {
            // Using '__name__' allows querying by document ID in a 'where' clause
            const bQuery = query(buildingsRef, where("__name__", "in", chunk));
            return getDocs(bQuery);
        });

        const buildingSnapshots = await Promise.all(buildingQueryPromises);
        const buildingMap = {};
        
        buildingSnapshots.forEach(snap => {
            snap.forEach(doc => {
                buildingMap[doc.id] = doc.data().name || "Unknown Building";
            });
        });

        // --- STEP 2: Fetch Records (Your Existing Logic) ---
        const recordQueryPromises = chunks.map(chunk => {
            const keyQuery = query(
                recordsRef,
                where("buildingID", "in", chunk),
                orderBy("issueTime", "desc")
            );
            return getDocs(keyQuery);
        });

        const querySnapshots = await Promise.all(recordQueryPromises);
        let resultArray = [];

        querySnapshots.forEach((snapshot) => {
            snapshot.forEach((doc) => {
                const key = doc.data();
                resultArray.push({
                    rawDate: key.issueTime.toDate(), 
                    issueTime: moment(key.issueTime.toDate()).format('MM/DD/YYYY hh:mm a'),
                    // --- NEW FIELD ADDED HERE ---
                    buildingName: buildingMap[key.buildingID] || "N/A",
                    buildingID: key.buildingID,
                    // ----------------------------
                    visitorCompany: key.visitorCompany,
                    visitorEmail: key.visitorEmail,
                    visitorName: key.visitorName,
                    visitorPhone: key.visitorPhoneNo,
                    keyNumber: key.keyNumber,
                    tag: key.keyTag,
                    status: key.status,
                    location: key.location,
                    notes: key.notes ? key.notes : [],
                    id: doc.id
                });
            });
        });

        resultArray.sort((a, b) => b.rawDate - a.rawDate);

        return { result: resultArray.map(({ rawDate, ...rest }) => rest), buildingMap };

    } catch (error) {
        console.error("Error fetching keys and buildings:", error);
        throw error;
    }
}

export const addNote = async (id, noteValue, currentNotes) => {
    const userFromStorage = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : '';
    try {

        const keyDocRef = doc(db, "records", id);
        currentNotes.push({
            'note' : noteValue,
            'timestamp' : Timestamp.fromDate(new Date()),
            'userId' : userFromStorage.userId,
            'userName' : userFromStorage.userName
        }); 

        await updateDoc(keyDocRef, {
            notes : currentNotes
        });

        return {
            status : true,
            message : "Updated Successfully"
        }

    } catch (err) {
        return {
            status : false,
            message : err
        }
    }
}

// Function to get first set of outstanding keys data based on search parameters
export const getAllKeyLoanData = async (buildingId) => {
    const docRef = collection(db, "returnedKeys");
    let keyQuery = query(docRef,
        where("buildingID", "==", buildingId),
        orderBy('issueTime', 'desc'));
    const querySnapshot = await getDocs(keyQuery);
    let resultArray = [];
    querySnapshot.forEach((doc) => {
        const key = doc.data();
        resultArray.push({
            issueTime : moment(key.issueTime.toDate()).format('MM/DD/YYYY hh:mm a'),
            returnTime : moment(key.returnTime.toDate()).format('MM/DD/YYYY hh:mm a'),
            visitorCompany : key.visitorCompany,
            visitorEmail : key.visitorEmail,
            visitorName : key.visitorName,
            visitorPhone : key.visitorPhoneNo,
            keyNumber : key.keyNumber,
            tag : key.keyTag,
            location : key.location,
            code: key.keyCode
        })
    });
    return {result : resultArray};
}
